import {
    Box,
    Grid,
    Typography,
    Divider,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    Button
} from '@mui/material';
import { DrawerHeader } from '../../../../../components/Drawer/DrawerHeader';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
    useLazyGetEmployeesResultsQuery,
    useLazyGetPdfResultadosExamenEmpleadoQuery,
    useGenerarPdfCertificadoAptitudMutation
} from '../../../../../services/periodicosApi';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { downloadBase64File, downloadFromBase64 } from '../../../../../utils/common';
import BackdropComponent from '../../../../../components/Backdrop/BackdropComponent';

interface PeriodicosResultadosDetailsHistoryDrawerProps {
    setOpen: (state: string) => void;
    selectedEmployee: any;
}

interface ResultadoItem {
    examenReconfirmacion: string;
    idExamenEmpleado: number | null;
    nombreExamen: string;
    fechaRealizacion: string;
    aptitud: string;
    observacion: string;
    rutaArchivoResultado: string | null;
}

interface ResultadosPorAnio {
    periodicos: ResultadoItem[];
    reconfirmaciones: ResultadoItem[];
}

const PeriodicosResultadosDetailsHistoryDrawer = ({
    setOpen,
    selectedEmployee
}: PeriodicosResultadosDetailsHistoryDrawerProps) => {
    const { t } = useTranslation('periodicos');
    const [triggerGetResults, { isFetching }] = useLazyGetEmployeesResultsQuery();

    const [resultadosPerYear, setResultadosPerYear] = useState<Record<string, ResultadosPorAnio>>(
        {}
    );
    const [generarPdfCertificadoAptitud] = useGenerarPdfCertificadoAptitudMutation();

    const [selectedYear, setSelectedYear] = useState<string>('');
    const [loadingYears, setLoadingYears] = useState(true);
    const [triggerDownloadPdf] = useLazyGetPdfResultadosExamenEmpleadoQuery();
    const handleCloseDrawer = () => setOpen('');
    const [downloading, setDownloading] = useState(false);

    const empleadoCuil = selectedEmployee?.cuil;

    const idEmpleado =
        selectedEmployee?.empleado?.id ??
        selectedEmployee?.idEmpleadoNomina ??
        selectedEmployee?.idEmpleadoExpuesto ??
        null;

    const idRiesgo = selectedEmployee?.riesgo?.id || null;

    const handleDownloadPdf = async (idExamenEmpleado: number) => {
        try {
            setDownloading(true);
            const response = await triggerDownloadPdf(idExamenEmpleado).unwrap();

            if (
                typeof response === 'object' &&
                'data' in response &&
                typeof (response as any).data === 'string'
            ) {
                const fileName = `Resultado_Exámen_${empleadoCuil}`;
                const fileUrl = (response as any).data;
                await downloadBase64File(fileUrl, idExamenEmpleado, fileName);
            } else {
                console.error(response);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadCertificado = async (idExamenEmpleado: number) => {
        try {
            setDownloading(true);
            const response = await generarPdfCertificadoAptitud(idExamenEmpleado).unwrap();

            if (response?.data) {
                const data = response.data;

                const fileName = `Certificado_de_Aptitud_${empleadoCuil}`;
                const isUrl = typeof data === 'string' && data.startsWith('http');
                const isBase64 = typeof data === 'string' && data.startsWith('JVBER');

                if (isUrl) {
                    await downloadBase64File(data, idExamenEmpleado, fileName);
                } else if (isBase64) {
                    downloadFromBase64(data, idExamenEmpleado, fileName);
                } else {
                    console.error(data);
                }
            } else {
                console.error(response);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setDownloading(false);
        }
    };

    const renderDownloadButton = (label: string, onClick: () => void) => (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#F5F5F5',
                borderRadius: '6px',
                padding: '8px 12px',
                marginTop: '5px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#EAEAEA' }
            }}
        >
            <Typography variant="body2">{label}</Typography>
            <DownloadOutlinedIcon />
        </Box>
    );

    useEffect(() => {
        if (idEmpleado && idRiesgo) {
            setLoadingYears(true);
            triggerGetResults({
                idEmpleado,
                idRiesgo,
                skip: 0,
                take: 50
            })
                .unwrap()
                .then((response) => {
                    const resultados = response?.data?.data || [];

                    const agrupados = resultados.reduce((acc: any, item: any) => {
                        if (!item.fechaRealizacion) return acc;

                        const year = new Date(item.fechaRealizacion).getFullYear();
                        if (!acc[year]) {
                            acc[year] = {
                                periodicos: [],
                                reconfirmaciones: []
                            };
                        }

                        const isReconfirmacion = item.examenReconfirmacion !== null;

                        const dataItem = {
                            idExamenEmpleado: item.id ?? item.idExamenEmpleado ?? null,
                            nombreExamen: isReconfirmacion
                                ? item.examenReconfirmacion
                                : item.examenImportacion?.descripcion || '-',
                            examenReconfirmacion: item.examenReconfirmacion,
                            fechaRealizacion: new Date(item.fechaRealizacion).toLocaleDateString(),
                            aptitud: item.aptitud?.nombre || '-',
                            observacion: item.observacion || '-',
                            rutaArchivoResultado: item.rutaArchivoResultado || null
                        };

                        if (isReconfirmacion) {
                            acc[year].reconfirmaciones.push(dataItem);
                        } else {
                            acc[year].periodicos.push(dataItem);
                        }

                        return acc;
                    }, {});

                    setResultadosPerYear(agrupados);

                    const years = Object.keys(agrupados).sort((a, b) => Number(b) - Number(a));
                    if (years.length > 0) setSelectedYear(years[0]);

                    setLoadingYears(false);
                })
                .catch(() => setLoadingYears(false));
        }
    }, [idEmpleado, idRiesgo, triggerGetResults]);

    const nombre = selectedEmployee?.nombre || '-';
    const riesgoCodigo = selectedEmployee?.riesgo?.codigo || '-';
    const riesgoDescripcion = selectedEmployee?.riesgo?.descripcion || '-';

    const years = Object.keys(resultadosPerYear).sort((a, b) => Number(b) - Number(a));
    const resultadosForYear = resultadosPerYear[selectedYear] || {
        periodicos: [],
        reconfirmaciones: []
    };
    const periodicos = resultadosForYear.periodicos || [];
    const reconfirmaciones = resultadosForYear.reconfirmaciones || [];

    return (
        <>
            <BackdropComponent loading={downloading} />
            <Box
                display="flex"
                flexDirection="column"
                height="100%"
                sx={{ backgroundColor: 'background.paper' }}
            >
                <DrawerHeader
                    onIconClick={handleCloseDrawer}
                    text={t('drawer.detailHistory.title', {
                        name: nombre,
                        riskCode: riesgoCodigo,
                        riskDescription: riesgoDescripcion
                    })}
                />

                <Box flex={1} overflow="auto" p={2}>
                    <Box sx={{ mb: 3 }}>
                        {loadingYears ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                                <CircularProgress size={16} />
                            </Box>
                        ) : years.length > 0 ? (
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('drawer.detailHistory.year')}</InputLabel>
                                <Select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                    label={t('drawer.detailHistory.year')}
                                >
                                    {years.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Typography
                                variant="body2"
                                fontStyle="italic"
                                color="text.secondary"
                                align="center"
                            >
                                {t('drawer.detailHistory.noYears')}
                            </Typography>
                        )}
                    </Box>

                    <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                        {t('drawer.detailHistory.periodicExams')}
                    </Typography>

                    {isFetching ? (
                        <Box display="flex" justifyContent="center" alignItems="center" py={5}>
                            <CircularProgress size={20} />
                        </Box>
                    ) : periodicos.length > 0 ? (
                        periodicos.map((examen, index) => (
                            <Box key={index} sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>{t('drawer.detailHistory.name')}:</strong>{' '}
                                    {examen.nombreExamen}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>{t('drawer.detailHistory.date')}:</strong>{' '}
                                    {examen.fechaRealizacion}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>{t('drawer.detailHistory.aptitude')}:</strong>{' '}
                                    {examen.aptitud}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>{t('drawer.detailHistory.observations')}:</strong>{' '}
                                    {examen.observacion}
                                </Typography>

                                <>
                                    {renderDownloadButton(
                                        t('drawer.detailHistory.downloadResult'),
                                        () => handleDownloadPdf(examen.idExamenEmpleado!)
                                    )}

                                    {renderDownloadButton(
                                        t('drawer.detailHistory.downloadCertificate'),
                                        () => handleDownloadCertificado(examen.idExamenEmpleado!)
                                    )}
                                </>

                                {index < periodicos.length - 1 && <Divider sx={{ mt: 2, mb: 2 }} />}
                            </Box>
                        ))
                    ) : (
                        !loadingYears && (
                            <Typography
                                variant="body2"
                                mt={3}
                                fontStyle="italic"
                                color="text.secondary"
                                align="center"
                            >
                                {t('drawer.detailHistory.noResults')}
                            </Typography>
                        )
                    )}

                    <Typography variant="subtitle1" fontWeight="bold" mt={4} mb={2}>
                        {t('drawer.detailHistory.reconfirmationsResults')}
                    </Typography>

                    {isFetching ? (
                        <Box display="flex" justifyContent="center" alignItems="center" py={5}>
                            <CircularProgress size={20} />
                        </Box>
                    ) : reconfirmaciones.length > 0 ? (
                        reconfirmaciones.map((examen, index) => (
                            <Box key={index} sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>{t('drawer.detailHistory.name')}:</strong>{' '}
                                    {examen.examenReconfirmacion}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>{t('drawer.detailHistory.date')}:</strong>{' '}
                                    {examen.fechaRealizacion}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>{t('drawer.detailHistory.aptitude')}:</strong>{' '}
                                    {examen.aptitud}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>{t('drawer.detailHistory.observations')}:</strong>{' '}
                                    {examen.observacion}
                                </Typography>

                                <>
                                    {renderDownloadButton(
                                        t('drawer.detailHistory.downloadResult'),
                                        () =>
                                            examen.idExamenEmpleado &&
                                            handleDownloadPdf(examen.idExamenEmpleado)
                                    )}
                                    {renderDownloadButton(
                                        t('drawer.detailHistory.downloadCertificate'),
                                        () =>
                                            examen.idExamenEmpleado &&
                                            handleDownloadCertificado(examen.idExamenEmpleado)
                                    )}
                                </>

                                {index < reconfirmaciones.length - 1 && (
                                    <Divider sx={{ mt: 2, mb: 2 }} />
                                )}
                            </Box>
                        ))
                    ) : (
                        !loadingYears && (
                            <Typography
                                variant="body2"
                                mt={2}
                                fontStyle="italic"
                                color="text.secondary"
                                align="center"
                            >
                                {t('drawer.detailHistory.noResults')}
                            </Typography>
                        )
                    )}
                </Box>

                <Box p={2} borderTop="1px solid #eee">
                    <Grid container justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleCloseDrawer}
                            sx={{ borderRadius: '8px' }}
                        >
                            {t('drawer.detailHistory.back')}
                        </Button>
                    </Grid>
                </Box>
            </Box>
        </>
    );
};

export default PeriodicosResultadosDetailsHistoryDrawer;
